import { cn } from '@/lib/utils'
import TaskStructureBadges from '@/frontend/features/Fde/TaskStructureBadges'
import {
  TaskCommentCount,
  TaskDueDate,
  TaskPeople,
  TaskPriority,
  TaskStatusIcon,
  taskCreatedDate,
  taskStatusTone,
} from '@/frontend/features/Fde/projectTaskPresentation'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'

interface Props {
  task: ProjectTask
  overdue: boolean
  onSelect: () => void
  copy: FdeCopy
  dragHandleProps?: DraggableProvidedDragHandleProps | null
}

export default function ProjectTaskCard({ task, overdue, onSelect, copy, dragHandleProps }: Props) {
  const created = copy.management.task_created_at.replace(
    '%{date}',
    taskCreatedDate(task.created_at),
  )
  const creator = copy.management.task_created_by.replace('%{name}', task.created_by_label)

  return (
    <button
      {...dragHandleProps}
      type="button"
      onClick={onSelect}
      aria-label={task.title}
      className={cn(
        'group flex w-full flex-col gap-3 rounded-lg border border-border/70 bg-background px-3.5 py-3 text-left shadow-sm transition-colors hover:border-foreground/20 hover:bg-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
        dragHandleProps && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="flex min-h-6 items-center justify-between gap-3">
        <span
          title={task.identifier}
          className="font-mono text-[10px] tracking-wide text-muted-foreground"
        >
          {task.identifier}
        </span>
        <TaskPeople
          people={task.assignees}
          emptyLabel={copy.management.task_unassigned}
          workStateLabels={copy.management.task_work_states}
          compact
        />
      </div>
      <div className="flex items-start gap-2">
        <span
          title={copy.management.statuses[task.status]}
          className={cn('mt-0.5 shrink-0', taskStatusTone[task.status].split(' ')[0])}
        >
          <TaskStatusIcon status={task.status} className="size-3.5" />
        </span>
        <p
          title={task.title}
          className="line-clamp-3 min-w-0 text-[13px] font-medium leading-5 text-foreground"
        >
          {task.title}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <TaskStructureBadges task={task} copy={copy} />
        <TaskPriority priority={task.priority} label={copy.management.priorities[task.priority]} />
        <TaskDueDate value={task.due_on} overdue={overdue} label={copy.management.task_due_on} />
      </div>
      <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <time dateTime={task.created_at} title={`${created} · ${creator}`} className="truncate">
          {created}
        </time>
        <TaskCommentCount count={task.comments.length} label={copy.management.comments} />
      </div>
    </button>
  )
}
